<script setup lang="ts">
import type { DropdownMenuItem, TabsItem } from '@nuxt/ui'
import type { InstanceRow } from '@/components/InstancesTable.vue'
import type { ChannelWithReleases, Customer, CustomerUsageSeries, FleetSeriesData, FleetTelemetry, FleetTimelineRow, Instance, Product } from '@/types'
import { Deferred, Head, Link, router, usePage } from '@inertiajs/vue3'
import { computed, ref, watch } from 'vue'
import ChannelsPanel from '@/components/channels/ChannelsPanel.vue'
import TimeRangeToolbar from '@/components/charts/TimeRangeToolbar.vue'
import InstancesTable from '@/components/InstancesTable.vue'
import SigningPanel from '@/components/products/SigningPanel.vue'
import FleetHistory from '@/components/telemetry/FleetHistory.vue'
import ProductUsageHistory from '@/components/telemetry/ProductUsageHistory.vue'
import TelemetryPanel from '@/components/telemetry/TelemetryPanel.vue'
import { provideTimeRange } from '@/composables/useTimeRange'
import { dashboardLayout } from '@/lib/layouts'
import {
  adminCustomerPath,
  adminProductPath,
  adminProductsPath,
  editAdminProductPath,
} from '@/routes'

defineOptions({
  layout: dashboardLayout,
})

const props = defineProps<{
  product: Product
  instances: ProductInstance[]
  telemetry: FleetTelemetry
  fleetSeries?: FleetSeriesData
  fleetTimeline?: FleetTimelineRow[]
  usageSeries?: CustomerUsageSeries
  customers: Pick<Customer, 'id' | 'name' | 'active'>[]
  channels: ChannelWithReleases[]
}>()

interface ProductInstance extends Instance {
  customer: Pick<Customer, 'id' | 'name' | 'active'>
}

const page = usePage()

// One time range drives both the Overview usage chart and the Telemetry tab
// (snapshot stays current-state; the historic charts re-bucket together on
// change), Grafana-style, so switching tabs never resets the window.
const timeRange = provideTimeRange({ only: ['fleetSeries', 'fleetTimeline', 'usageSeries'] })

// Tab state is mirrored into the URL (?tab=) so a specific tab is shareable
// and survives a refresh, without a full Inertia round-trip.
function tabFromUrl() {
  return new URL(page.url, 'http://localhost').searchParams.get('tab') ?? 'overview'
}

const activeTab = ref(tabFromUrl())

watch(activeTab, (tab) => {
  const url = new URL(window.location.href)
  if (tab === 'overview')
    url.searchParams.delete('tab')
  else
    url.searchParams.set('tab', tab)
  window.history.replaceState(window.history.state, '', url)
})

const tabItems = computed<TabsItem[]>(() => [
  { label: 'Overview', icon: 'i-lucide-info', value: 'overview', slot: 'overview' },
  {
    label: 'Licenses & instances',
    icon: 'i-lucide-key-round',
    value: 'licenses',
    slot: 'licenses',
    badge: { label: String(props.instances.length), color: 'neutral', variant: 'subtle' },
  },
  {
    label: 'Channels & releases',
    icon: 'i-lucide-git-branch',
    value: 'channels',
    slot: 'channels',
    badge: { label: String(props.channels.length), color: 'neutral', variant: 'subtle' },
  },
  {
    label: 'Signing',
    icon: 'i-lucide-shield-check',
    value: 'signing',
    slot: 'signing',
  },
  {
    label: 'Telemetry',
    icon: 'i-lucide-activity',
    value: 'telemetry',
    slot: 'telemetry',
    badge: { label: String(props.telemetry.total), color: 'neutral', variant: 'subtle' },
  },
])

const instanceRows = computed<InstanceRow[]>(() =>
  props.instances.map(instance => ({
    id: instance.id,
    name: instance.customer.name,
    identifier: instance.identifier,
    metadata: instance.metadata,
    channel_id: instance.channel_id,
    reported_version: instance.reported_version,
    reported_at: instance.reported_at,
    channels: instance.channels,
    created_at: instance.created_at,
    to: adminCustomerPath(instance.customer.id),
    active: instance.customer.active,
  })),
)

const deleteOpen = ref(false)
const deleting = ref(false)

function destroy() {
  router.delete(adminProductPath(props.product.slug), {
    onStart: () => (deleting.value = true),
    onFinish: () => (deleting.value = false),
  })
}

const menuItems = [
  [
    {
      label: 'Edit',
      icon: 'i-lucide-pencil',
      onSelect: () => router.visit(editAdminProductPath(props.product.slug)),
    },
    {
      label: 'Delete',
      icon: 'i-lucide-trash-2',
      color: 'error',
      onSelect: () => (deleteOpen.value = true),
    },
  ],
] satisfies DropdownMenuItem[][]

function formatDate(value: string) {
  return new Date(value).toLocaleDateString()
}
</script>

<template>
  <Head :title="product.name" />

  <UDashboardPanel id="product-show">
    <template #header>
      <UDashboardNavbar :title="product.name">
        <template #leading>
          <UButton
            :to="adminProductsPath()"
            :as="Link"
            icon="i-lucide-arrow-left"
            color="neutral"
            variant="ghost"
            square
          />
        </template>

        <template #right>
          <UButton
            :to="editAdminProductPath(product.slug)"
            :as="Link"
            label="Edit"
            icon="i-lucide-pencil"
            color="neutral"
            variant="subtle"
          />
          <UDropdownMenu :items="menuItems">
            <UButton
              icon="i-lucide-ellipsis-vertical"
              color="neutral"
              variant="ghost"
              square
            />
          </UDropdownMenu>
        </template>
      </UDashboardNavbar>
    </template>

    <template #body>
      <UTabs
        v-model="activeTab"
        :items="tabItems"
        variant="link"
        :unmount-on-hide="false"
        class="gap-6"
        :ui="{ list: 'overflow-x-auto' }"
      >
        <template #overview>
          <div class="flex flex-col gap-8">
            <div class="ring-default rounded-lg p-5 ring">
              <dl class="grid gap-4 sm:grid-cols-2">
                <div>
                  <dt class="text-muted text-xs uppercase tracking-wide">
                    Slug
                  </dt>
                  <dd class="text-highlighted font-mono">
                    {{ product.slug }}
                  </dd>
                </div>
                <div>
                  <dt class="text-muted text-xs uppercase tracking-wide">
                    Created
                  </dt>
                  <dd class="text-highlighted">
                    {{ formatDate(product.created_at) }}
                  </dd>
                </div>
                <div class="sm:col-span-2">
                  <dt class="text-muted text-xs uppercase tracking-wide">
                    Description
                  </dt>
                  <dd class="text-highlighted whitespace-pre-line">
                    {{ product.description || '—' }}
                  </dd>
                </div>
              </dl>
            </div>

            <div class="flex flex-col gap-8">
              <div class="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h2 class="text-highlighted font-semibold">
                    Token usage
                  </h2>
                  <p class="text-muted text-sm">
                    Billable token usage for {{ product.name }} over the selected range, by customer.
                  </p>
                </div>
                <TimeRangeToolbar :controller="timeRange" />
              </div>

              <Deferred :data="['usageSeries']">
                <template #fallback>
                  <USkeleton class="h-80 w-full" />
                </template>

                <ProductUsageHistory
                  v-if="usageSeries"
                  :series="usageSeries"
                  :resolved="timeRange.resolved.value"
                  :loading="timeRange.loading.value"
                />
              </Deferred>
            </div>
          </div>
        </template>

        <template #licenses>
          <div class="flex flex-col gap-4">
            <div class="flex items-end justify-between gap-4">
              <div>
                <h2 class="text-highlighted font-semibold">
                  Customer instances
                </h2>
                <p class="text-muted text-sm">
                  Customers licensed to use {{ product.name }}.
                </p>
              </div>
            </div>

            <InstancesTable
              :rows="instanceRows"
              noun="customer"
              empty-text="No customer instances for this product yet."
            />
          </div>
        </template>

        <template #channels>
          <ChannelsPanel :product="product" :channels="channels" />
        </template>

        <template #signing>
          <SigningPanel :product="product" />
        </template>

        <template #telemetry>
          <div class="flex flex-col gap-8">
            <div class="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 class="text-highlighted font-semibold">
                  Fleet telemetry
                </h2>
                <p class="text-muted text-sm">
                  Current state, plus how the fleet moved over the selected range.
                </p>
              </div>
              <TimeRangeToolbar :controller="timeRange" />
            </div>

            <TelemetryPanel :telemetry="telemetry" />

            <Deferred v-if="telemetry.total > 0" :data="['fleetSeries', 'fleetTimeline']">
              <template #fallback>
                <div class="grid gap-8 xl:grid-cols-2">
                  <USkeleton v-for="n in 2" :key="n" class="h-72 w-full" />
                </div>
              </template>

              <FleetHistory
                v-if="fleetSeries && fleetTimeline"
                :series="fleetSeries"
                :timeline="fleetTimeline"
                :instances="instances"
                :resolved="timeRange.resolved.value"
                :loading="timeRange.loading.value"
              />
            </Deferred>
          </div>
        </template>
      </UTabs>
    </template>
  </UDashboardPanel>

  <UModal
    v-model:open="deleteOpen"
    title="Delete product"
    :description="`Delete &quot;${product.name}&quot;? This cannot be undone.`"
  >
    <template #footer>
      <div class="flex justify-end gap-3">
        <UButton
          label="Cancel"
          color="neutral"
          variant="ghost"
          @click="deleteOpen = false"
        />
        <UButton
          label="Delete"
          color="error"
          icon="i-lucide-trash-2"
          :loading="deleting"
          @click="destroy"
        />
      </div>
    </template>
  </UModal>
</template>
